// Icons come straight from lucide-react (the same line set `compose/Icon`
// re-exports) — imported here directly so this chunk never edits the shared,
// concurrently-owned icon registry. Stroke pinned to the 1.5px quiet register.
import { AtSign, CheckCircle2, Inbox, Reply, UserPlus } from "lucide-react";
import type { NotificationItem } from "../api/types";
import { relativeTime } from "../lib/format";

/**
 * The presentational dropdown body — a pure list of notification rows (no data
 * fetching, no routing), so it renders identically under SSR test markup and in
 * the live bell. Each row is a button (`data-testid="notif-item-<id>"`) that
 * calls `onSelect`; unread rows carry a dot + the `--unread` modifier. An empty
 * feed shows the caught-up state.
 */

function iconFor(kind: string): React.ReactNode {
  switch (kind) {
    case "mention":
      return <AtSign size={15} strokeWidth={1.5} />;
    case "reply":
      return <Reply size={15} strokeWidth={1.5} />;
    case "status-change":
      return <CheckCircle2 size={15} strokeWidth={1.5} />;
    case "assignment":
      return <UserPlus size={15} strokeWidth={1.5} />;
    default:
      return <Inbox size={15} strokeWidth={1.5} />;
  }
}

export interface NotificationListProps {
  items: NotificationItem[];
  onSelect: (n: NotificationItem) => void;
  /** Injectable clock for deterministic relative-time under test. */
  nowMs?: number;
}

export function NotificationList({
  items,
  onSelect,
  nowMs,
}: NotificationListProps) {
  if (items.length === 0) {
    return (
      <div className="notif__empty" data-testid="notif-empty">
        <Inbox size={20} strokeWidth={1.5} />
        <span>You&rsquo;re all caught up.</span>
      </div>
    );
  }
  return (
    <ul className="notif__list" data-testid="notif-list">
      {items.map((n) => {
        const unread = !n.read_at;
        return (
          <li key={n.id}>
            <button
              type="button"
              className={`notif__item${unread ? " notif__item--unread" : ""}`}
              data-testid={`notif-item-${n.id}`}
              data-kind={n.kind}
              data-read={unread ? "false" : "true"}
              onClick={() => onSelect(n)}
            >
              <span className="notif__icon" aria-hidden="true">
                {iconFor(n.kind)}
              </span>
              <span className="notif__text">
                <span className="notif__summary">{n.summary}</span>
                <span className="notif__meta">
                  {/* "short-date": past a week a notification row shows the day
                      it happened, not "10d ago" — it is a thing you act on. */}
                  {relativeTime(n.created_at, nowMs, "short-date")}
                </span>
              </span>
              {unread ? (
                <span
                  className="notif__dot"
                  data-testid={`notif-dot-${n.id}`}
                  aria-label="unread"
                />
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
